import { FilesetResolver, ImageSegmenter } from '@mediapipe/tasks-vision';
import { VirtualBackgroundProcessor, ImageFit, GaussianBlurBackgroundProcessor } from '@twilio/video-processors';
import { LocalVideoTrack } from 'twilio-video';

class MediapipeImageSegmenter {
  constructor(options) {
    this.options = options;
  }
  createImageSegmenterPromise;
  imageSegmenter;
  labels = [];
  async createImageSegmenter(options = {}) {
    const taskCanvas = document.createElement("canvas");
    const taskVisionWasmPath = this.options.taskVisionWasmPath || "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm";
    const modelAssetPath = this.options.modelAssetPath || "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite";
    const vision = await FilesetResolver.forVisionTasks(taskVisionWasmPath);
    this.imageSegmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath,
        delegate: "GPU"
      },
      runningMode: "VIDEO",
      canvas: taskCanvas,
      outputCategoryMask: false,
      outputConfidenceMasks: true,
      ...options
    });
    this.labels = this.imageSegmenter.getLabels();
  }
}

function loadImage(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.src = url;
    image.crossOrigin = "anonymous";
    image.onload = () => {
      resolve(image);
    };
  });
}
function loadVideo(url) {
  return new Promise((resolve) => {
    const videoElement = document.createElement("video");
    videoElement.src = url;
    videoElement.autoplay = true;
    videoElement.loop = true;
    videoElement.muted = true;
    videoElement.style.objectFit = "cover";
    videoElement.play().catch(console.error);
    videoElement.addEventListener("loadeddata", () => {
      videoElement.width = videoElement.videoWidth;
      videoElement.height = videoElement.videoHeight;
      resolve(videoElement);
    });
  });
}

class MediapipeVirtualBackground extends MediapipeImageSegmenter {
  constructor(options) {
    super(options);
    this.options = options;
    this.canvas = options.canvas;
    this.video = options.video;
    this.canvasCtx = this.canvas.getContext("2d");
    this.backgroundImageBounds = this.defaultBackgroundImageBounds;
  }
  canvas;
  canvasCtx;
  video;
  backgroundImage = new Image();
  backgroundImageFit;
  backgroundImageBounds;
  get isVirtualBackgroundImage() {
    return this.options.isVirtualBackgroundImage ?? false;
  }
  set isVirtualBackgroundImage(value) {
    this.options.isVirtualBackgroundImage = value;
  }
  get isMirrorVideo() {
    return this.options.isMirrorVideo ?? false;
  }
  set isMirrorVideo(value) {
    this.options.isMirrorVideo = value;
  }
  get edgeBlurAmount() {
    return this.options?.edgeBlurAmount ?? 8;
  }
  set edgeBlurAmount(value) {
    this.options.edgeBlurAmount = value;
  }
  get backgroundBlurAmount() {
    return this.options.backgroundBlurAmount ?? 20;
  }
  set backgroundBlurAmount(value) {
    this.options.backgroundBlurAmount = value;
  }
  get defaultBackgroundImageBounds() {
    return {
      x: 0,
      y: 0,
      width: this.canvas.width,
      height: this.canvas.height
    };
  }
  async draw() {
    if (!this.createImageSegmenterPromise)
      this.createImageSegmenterPromise = this.createImageSegmenter();
    await this.createImageSegmenterPromise;
    this.canvasCtx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
    const startTimeMs = performance.now();
    const result = this.imageSegmenter?.segmentForVideo(this.video, startTimeMs);
    if (result?.confidenceMasks) {
      const imageData = this.canvasCtx.getImageData(
        0,
        0,
        this.video.videoWidth,
        this.video.videoHeight
      ).data;
      const personIndex = this.labels.indexOf("selfie");
      const personMask = result.confidenceMasks[personIndex].getAsFloat32Array();
      let j = 0;
      for (let i = 0; i < personMask.length; ++i) {
        const confidence = personMask[i];
        const edgeSoftness = this.isVirtualBackgroundImage ? this.edgeBlurAmount : 1.5;
        const adjustedConfidence = confidence ** edgeSoftness;
        imageData[j + 3] = adjustedConfidence * 255;
        j += 4;
      }
      const uint8Array = new Uint8ClampedArray(imageData.buffer);
      const dataNew = new ImageData(
        uint8Array,
        this.video.videoWidth,
        this.video.videoHeight
      );
      const width = this.canvas.width;
      const height = this.canvas.height;
      this.canvasCtx.save();
      if (this.isMirrorVideo) {
        const originalData = dataNew.data;
        const mirroredImageData = this.canvasCtx.createImageData(width, height);
        const mirroredData = mirroredImageData.data;
        for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            const sourceIndex = (y * width + x) * 4;
            const destX = width - 1 - x;
            const destIndex = (y * width + destX) * 4;
            mirroredData[destIndex] = originalData[sourceIndex];
            mirroredData[destIndex + 1] = originalData[sourceIndex + 1];
            mirroredData[destIndex + 2] = originalData[sourceIndex + 2];
            mirroredData[destIndex + 3] = originalData[sourceIndex + 3];
          }
        }
        this.canvasCtx.putImageData(mirroredImageData, 0, 0);
      } else {
        this.canvasCtx.putImageData(dataNew, 0, 0);
      }
      this.canvasCtx.globalCompositeOperation = "destination-over";
      if (this.isVirtualBackgroundImage) {
        this.drawBackgroundImage();
      } else {
        if (this.isMirrorVideo) {
          this.canvasCtx.scale(-1, 1);
          this.canvasCtx.translate(-this.canvas.width, 0);
        }
        this.canvasCtx.filter = `blur(${this.backgroundBlurAmount}px)`;
        this.canvasCtx.drawImage(this.video, 0, 0, width, height);
      }
      this.canvasCtx.restore();
      result.confidenceMasks.forEach((mask) => {
        mask.close();
      });
    }
  }
  setBackgroundImageFit(fit) {
    this.backgroundImageFit = fit;
    this.resetBackgroundImageBounds();
  }
  async loadBackgroundImage(url) {
    this.backgroundImage = await loadImage(url);
    this.resetBackgroundImageBounds();
  }
  async loadBackgroundVideo(url) {
    this.backgroundImage = await loadVideo(url);
    this.resetBackgroundImageBounds();
  }
  resetBackgroundImageBounds() {
    switch (this.backgroundImageFit) {
      case "contain":
        this.backgroundImageBounds = this.generateBackgroundImageContainBounds();
        break;
      case "cover":
        this.backgroundImageBounds = this.generateBackgroundImageCoverBounds();
        break;
      default:
        this.backgroundImageBounds = this.defaultBackgroundImageBounds;
        break;
    }
  }
  generateBackgroundImageContainBounds() {
    const { width, height } = this.canvas;
    const { width: imageWidth, height: imageHeight } = this.backgroundImage;
    const ratio = imageWidth / imageHeight;
    if (imageWidth > imageHeight) {
      const heightContain = width / ratio;
      return {
        x: 0,
        y: (height - heightContain) / 2,
        width,
        height: heightContain
      };
    } else {
      const widthContain = height * ratio;
      return {
        x: (width - widthContain) / 2,
        y: 0,
        width: widthContain,
        height
      };
    }
  }
  generateBackgroundImageCoverBounds() {
    const { width: canvasWidth, height: canvasHeight } = this.canvas;
    const { width: imageWidth, height: imageHeight } = this.backgroundImage;
    const imageRatio = imageWidth / imageHeight;
    const canvasRatio = canvasWidth / canvasHeight;
    let x = 0;
    let y = 0;
    let width = canvasWidth;
    let height = canvasHeight;
    if (imageRatio > canvasRatio) {
      width = canvasHeight * imageRatio;
      x = (canvasWidth - width) / 2;
    } else {
      height = canvasWidth / imageRatio;
      y = (canvasHeight - height) / 2;
    }
    return { x, y, width, height };
  }
  drawBackgroundImage() {
    this.canvasCtx.save();
    this.canvasCtx.filter = "none";
    this.canvasCtx.drawImage(
      this.backgroundImage,
      this.backgroundImageBounds.x,
      this.backgroundImageBounds.y,
      this.backgroundImageBounds.width,
      this.backgroundImageBounds.height
    );
    this.canvasCtx.restore();
  }
}

class RemoveBackground extends MediapipeImageSegmenter {
  constructor(options = {}) {
    super(options);
    this.options = options;
    this.canvas = document.createElement("canvas");
    this.canvasCtx = this.canvas.getContext("2d");
  }
  canvas;
  canvasCtx;
  get edgeBlurAmount() {
    return this.options?.edgeBlurAmount ?? 8;
  }
  set edgeBlurAmount(value) {
    this.options.edgeBlurAmount = value;
  }
  async remove(image) {
    if (!this.createImageSegmenterPromise) {
      this.createImageSegmenterPromise = this.createImageSegmenter({
        runningMode: "IMAGE"
      });
    }
    await this.createImageSegmenterPromise;
    let imageElement;
    if (typeof image === "string")
      imageElement = await loadImage(image);
    else
      imageElement = image;
    this.canvas.width = imageElement.width;
    this.canvas.height = imageElement.height;
    this.canvasCtx.drawImage(imageElement, 0, 0, this.canvas.width, this.canvas.height);
    const result = this.imageSegmenter?.segment(imageElement);
    if (result?.confidenceMasks) {
      const imageData = this.canvasCtx.getImageData(
        0,
        0,
        imageElement.width,
        imageElement.height
      ).data;
      const personIndex = this.labels.indexOf("selfie");
      const personMask = result.confidenceMasks[personIndex].getAsFloat32Array();
      let j = 0;
      for (let i = 0; i < personMask.length; ++i) {
        const confidence = personMask[i];
        const edgeSoftness = 1.5;
        const adjustedConfidence = confidence ** edgeSoftness;
        imageData[j + 3] = adjustedConfidence * 255;
        j += 4;
      }
      const uint8Array = new Uint8ClampedArray(imageData.buffer);
      const dataNew = new ImageData(uint8Array, imageElement.width, imageElement.height);
      this.canvasCtx.putImageData(dataNew, 0, 0);
    }
    return this.canvas.toDataURL("image/png");
  }
}

class TwilioVirtualBackground {
  constructor(options) {
    this.options = options;
    this.canvasCtx = this.canvas.getContext("2d");
    this.initOffscreenCanvas();
    this.initLocalVideoTrack();
  }
  canvasCtx = null;
  offscreenCanvas;
  offscreenCanvasCtx = null;
  localVideoTrack;
  localVideoElement = null;
  startVirtualBackgroundProcessorPromise;
  startBlurBackgroundProcessorPromise;
  virtualBackgroundProcessor;
  blurBackgroundProcessor;
  get assetsPath() {
    return this.options.assetsPath;
  }
  get canvas() {
    return this.options.canvas;
  }
  get video() {
    return this.options.video;
  }
  get isMirrorVideo() {
    return this.options.isMirrorVideo ?? false;
  }
  set isMirrorVideo(value) {
    this.options.isMirrorVideo = value;
  }
  get edgeBlurAmount() {
    return this.options.edgeBlurAmount ?? 8;
  }
  set edgeBlurAmount(value) {
    this.options.edgeBlurAmount = value;
    if (this.virtualBackgroundProcessor) {
      this.virtualBackgroundProcessor.maskBlurRadius = value;
    }
    if (this.blurBackgroundProcessor) {
      this.blurBackgroundProcessor.maskBlurRadius = value;
    }
  }
  get backgroundBlurAmount() {
    return this.options.backgroundBlurAmount ?? 20;
  }
  set backgroundBlurAmount(value) {
    this.options.backgroundBlurAmount = value;
    if (this.blurBackgroundProcessor) {
      this.blurBackgroundProcessor.blurFilterRadius = value;
    }
  }
  async setProcessor(options) {
    if (!this.localVideoTrack) {
      return;
    }
    const { type, backgroundImage } = options;
    let processor = null;
    if (type === "none") {
      processor = null;
    } else if (type === "image") {
      if (!this.virtualBackgroundProcessor) {
        this.virtualBackgroundProcessor = await this.startVirtualBackgroundProcessor();
      }
      if (backgroundImage) {
        await this.loadBackgroundImage(backgroundImage);
      }
      processor = this.virtualBackgroundProcessor;
    } else if (type === "blur") {
      if (!this.blurBackgroundProcessor) {
        this.blurBackgroundProcessor = await this.startBlurBackgroundProcessor();
      }
      processor = this.blurBackgroundProcessor;
    }
    if (this.localVideoTrack.processor === processor) {
      return;
    }
    if (this.localVideoTrack.processor) {
      this.localVideoTrack.removeProcessor(this.localVideoTrack.processor);
    }
    if (processor) {
      this.localVideoTrack.addProcessor(processor, {
        inputFrameBufferType: "videoframe",
        outputFrameBufferContextType: "bitmaprenderer"
      });
    }
  }
  async draw() {
    if (!this.canvasCtx || !this.localVideoTrack || !this.localVideoElement) {
      return;
    }
    if (this.startVirtualBackgroundProcessorPromise) {
      await this.startVirtualBackgroundProcessorPromise;
    }
    if (this.startBlurBackgroundProcessorPromise) {
      await this.startBlurBackgroundProcessorPromise;
    }
    this.canvasCtx.drawImage(
      this.localVideoElement,
      0,
      0,
      this.canvas.width,
      this.canvas.height
    );
  }
  async loadBackgroundImage(imageUrl) {
    if (this.virtualBackgroundProcessor) {
      this.virtualBackgroundProcessor.backgroundImage = await loadImage(imageUrl);
    }
  }
  initOffscreenCanvas() {
    this.offscreenCanvas = document.createElement("canvas");
    this.offscreenCanvas.width = this.video.videoWidth;
    this.offscreenCanvas.height = this.video.videoHeight;
    this.offscreenCanvasCtx = this.offscreenCanvas.getContext("2d");
    if (!this.offscreenCanvasCtx) {
      throw new Error("TwilioVirtualBackground.initOffscreenCanvas: Failed to get 2D context from canvas");
    }
    this.drawOffscreenCanvas();
  }
  drawOffscreenCanvas() {
    if (!this.offscreenCanvasCtx || !this.offscreenCanvas) {
      return;
    }
    this.offscreenCanvasCtx.save();
    if (this.isMirrorVideo) {
      this.offscreenCanvasCtx.scale(-1, 1);
      this.offscreenCanvasCtx.translate(-this.offscreenCanvas.width, 0);
    }
    this.offscreenCanvasCtx.drawImage(
      this.video,
      0,
      0,
      this.video.videoWidth,
      this.video.videoHeight
    );
    this.offscreenCanvasCtx.restore();
    requestAnimationFrame(this.drawOffscreenCanvas.bind(this));
  }
  initLocalVideoTrack() {
    if (!this.offscreenCanvas) {
      return;
    }
    const canvasStream = this.offscreenCanvas.captureStream(30);
    const videoTrack = canvasStream.getVideoTracks()[0];
    this.localVideoTrack = new LocalVideoTrack(videoTrack);
    this.localVideoElement = this.localVideoTrack.attach();
  }
  async startVirtualBackgroundProcessor() {
    if (!this.startVirtualBackgroundProcessorPromise) {
      this.startVirtualBackgroundProcessorPromise = (async () => {
        const processor = new VirtualBackgroundProcessor({
          assetsPath: this.assetsPath,
          backgroundImage: await loadImage("data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"),
          maskBlurRadius: this.edgeBlurAmount,
          fitType: ImageFit.Cover
        });
        await processor.loadModel();
        return processor;
      })();
    }
    return this.startVirtualBackgroundProcessorPromise;
  }
  async startBlurBackgroundProcessor() {
    if (!this.startBlurBackgroundProcessorPromise) {
      this.startBlurBackgroundProcessorPromise = (async () => {
        const processor = new GaussianBlurBackgroundProcessor({
          assetsPath: this.assetsPath,
          blurFilterRadius: this.backgroundBlurAmount,
          maskBlurRadius: this.edgeBlurAmount
        });
        await processor.loadModel();
        return processor;
      })();
    }
    return this.startBlurBackgroundProcessorPromise;
  }
}

const VirtualBackground = MediapipeVirtualBackground;

export { MediapipeVirtualBackground, RemoveBackground, TwilioVirtualBackground, VirtualBackground };
